import { Component, inject, signal, computed, OnInit, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { TagModule } from 'primeng/tag';
import { InputTextModule } from 'primeng/inputtext';
import { CheckboxModule } from 'primeng/checkbox';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { MenuService, MenuNode, MenuRequest } from '../../services/menu';
import { DropdownService, DropdownItem } from '../../services/dropdown';
import { ToastService } from '../../services/toast';
import { PageTitleService } from '../../services/page-title';

@Component({
  selector: 'app-menu-master',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    DialogModule,
    SelectModule,
    ButtonModule,
    TooltipModule,
    TagModule,
    InputTextModule,
    CheckboxModule,
    IconFieldModule,
    InputIconModule,
  ],
  templateUrl: './menu-master.html',
  styleUrls: ['./menu-master.css'],
})
export class MenuMaster implements OnInit {
  private fb = inject(FormBuilder);
  private menuService = inject(MenuService);
  private dropdown = inject(DropdownService);
  private toast = inject(ToastService);
  private platformId = inject(PLATFORM_ID);
  /** Page heading — pulled from the menu so it always matches the clicked item. */
  readonly pageTitle = inject(PageTitleService).title;

  loading = signal(false);
  saving = signal(false);
  tree = signal<MenuNode[]>([]);
  expanded = signal<Set<number>>(new Set());
  parentOptions = signal<DropdownItem[]>([]);

  // modal
  showModal = signal(false);
  editing = signal(false);
  modalTitle = signal('Add Menu');

  // icon picker
  showIconPicker = signal(false);
  iconSearch = signal('');
  readonly icons: string[] = [
    'home', 'th-large', 'list', 'table', 'th', 'bars', 'sitemap', 'folder', 'folder-open', 'file',
    'file-edit', 'copy', 'clone', 'bookmark', 'tag', 'tags', 'inbox', 'briefcase', 'building',
    'shop', 'shopping-cart', 'shopping-bag', 'wallet', 'credit-card', 'money-bill', 'dollar',
    'chart-bar', 'chart-line', 'chart-pie', 'percentage', 'calculator', 'sliders-h', 'cog',
    'wrench', 'shield', 'lock', 'lock-open', 'key', 'verified', 'check-circle', 'ban', 'eye',
    'eye-slash', 'user', 'users', 'user-plus', 'user-edit', 'id-card', 'address-book', 'phone',
    'mobile', 'envelope', 'send', 'comment', 'comments', 'bell', 'megaphone', 'whatsapp',
    'globe', 'map', 'map-marker', 'compass', 'flag', 'calendar', 'clock', 'history', 'sync',
    'refresh', 'bolt', 'star', 'heart', 'thumbs-up', 'gift', 'trophy', 'box', 'database',
    'server', 'cloud', 'download', 'upload', 'paperclip', 'print', 'search', 'filter', 'sort',
    'plus', 'minus', 'pencil', 'trash', 'save', 'check', 'times', 'info-circle', 'question-circle',
    'exclamation-triangle', 'cog', 'palette', 'image', 'video', 'camera', 'microphone', 'book',
    'graduation-cap', 'car', 'truck', 'heart-fill', 'circle', 'th-list', 'desktop', 'tablet',
  ];

  filteredIcons = computed(() => {
    const q = this.iconSearch().trim().toLowerCase();
    // De-duplicate: the @for tracks by the class string, so a repeated icon name
    // (e.g. 'cog' listed twice) would throw NG0955 "duplicated keys".
    const all = [...new Set(this.icons.map((n) => `pi pi-${n}`))];
    if (!q) return all;
    return all.filter((cls) => cls.toLowerCase().includes(q));
  });

  get selectedIcon(): string {
    return this.form.get('icon')?.value || 'pi pi-circle';
  }

  openIconPicker() {
    this.iconSearch.set('');
    this.showIconPicker.set(true);
  }

  pickIcon(cls: string) {
    this.form.get('icon')?.setValue(cls);
    this.showIconPicker.set(false);
  }

  form: FormGroup = this.fb.group({
    menuId: [0],
    parentId: [null as number | null],
    title: ['', Validators.required],
    route: [''],
    icon: ['pi pi-circle'],
    displayOrder: [0],
    permissionCode: [''],
    isActive: [true],
  });

  ngOnInit() {
    // Skip the API call during SSR / prerender (no backend there).
    if (isPlatformBrowser(this.platformId)) {
      this.load();
      this.dropdown.menus().subscribe((opts) => this.parentOptions.set(opts));
    }
  }

  load() {
    this.loading.set(true);
    this.menuService.getTree().subscribe({
      next: (res: any) => {
        this.loading.set(false);
        const data = res?.data ?? res ?? [];
        this.tree.set(Array.isArray(data) ? data : []);
        // expand all parents by default
        const ids = new Set<number>();
        this.tree().forEach((n) => n.children?.length && ids.add(n.menuId));
        this.expanded.set(ids);
      },
      error: () => this.loading.set(false),
    });
  }

  isExpanded(id: number) {
    return this.expanded().has(id);
  }

  toggleExpand(id: number) {
    const set = new Set(this.expanded());
    set.has(id) ? set.delete(id) : set.add(id);
    this.expanded.set(set);
  }

  // ---- modal openers ----
  openAddRoot() {
    this.editing.set(false);
    this.modalTitle.set('Add Menu');
    this.form.reset({
      menuId: 0,
      parentId: null,
      title: '',
      route: '',
      icon: 'pi pi-circle',
      displayOrder: 0,
      permissionCode: '',
      isActive: true,
    });
    this.showModal.set(true);
  }

  openAddChild(parent: MenuNode) {
    this.editing.set(false);
    this.modalTitle.set(`Add Submenu under "${parent.title}"`);
    this.form.reset({
      menuId: 0,
      parentId: parent.menuId,
      title: '',
      route: '',
      icon: 'pi pi-circle',
      displayOrder: 0,
      permissionCode: '',
      isActive: true,
    });
    this.showModal.set(true);
  }

  openEdit(node: MenuNode) {
    this.editing.set(true);
    this.modalTitle.set(`Edit "${node.title}"`);
    this.form.reset({
      menuId: node.menuId,
      parentId: node.parentId ?? null,
      title: node.title,
      route: node.route ?? '',
      icon: node.icon ?? 'pi pi-circle',
      displayOrder: node.displayOrder ?? 0,
      permissionCode: node.permissionCode ?? '',
      isActive: node.isActive ?? true,
    });
    this.showModal.set(true);
  }

  closeModal() {
    this.showModal.set(false);
  }

  save() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.toast.warning('Menu title is required');
      return;
    }

    this.saving.set(true);
    const payload = this.form.value as MenuRequest;

    this.menuService.save(payload).subscribe({
      next: (res: any) => {
        this.saving.set(false);
        if (res?.success === false) {
          this.toast.error(res?.message || 'Failed to save menu');
          return;
        }
        this.toast.success(this.editing() ? 'Menu updated' : 'Menu created');
        this.closeModal();
        this.load();
      },
      error: () => this.saving.set(false),
    });
  }

  async remove(node: MenuNode) {
    const hasChildren = !!node.children?.length;
    const msg = hasChildren
      ? `"${node.title}" and its ${node.children!.length} submenu(s) will be deleted.`
      : `"${node.title}" will be deleted.`;

    const ok = await this.toast.confirm(msg, 'Delete menu?', 'Delete');
    if (!ok) return;

    this.menuService.delete(node.menuId).subscribe({
      next: (res: any) => {
        if (res?.success === false) {
          this.toast.error(res?.message || 'Failed to delete menu');
          return;
        }
        this.toast.success('Menu deleted');
        this.load();
      },
    });
  }
}
